/**
 * Per-page copy for the signed-out selling layer and the "About this data"
 * block: what the visitor is looking at, what ORTEX Advanced adds on THIS
 * page, and plain definitions of the metrics on it.
 *
 * One table drives three surfaces, so they cannot drift apart:
 *   - `LockedValuePanel` (signed-out visitors only): headline, unlock bullets, offer;
 *   - `AboutThisData` (server-rendered, so crawlers and AI assistants can read and
 *     quote the definitions without logging in);
 *   - the page's Dataset / DefinedTermSet JSON-LD.
 *
 * Keys are Next.js pathname patterns (`router.pathname`), so one entry covers
 * every ticker. Pages not listed here get neither surface.
 *
 * Wording rule: describe what the data IS. ORTEX sells data, not advice; ad
 * review files "signals / ideas" wording under regulated investing.
 */

export interface StockRef {
    name: string;
    ticker: string;
    exchangesymbol: string;
}

export interface MetricDefinition {
    term: string;
    definition: string;
}

export interface LockedPage {
    /** Stable id, sent with every tracking event. */
    id: string;
    /** Short metric name, e.g. "Short Interest". */
    metric: string;
    /** Panel headline: what the visitor is looking at. */
    headline: (stock?: StockRef) => string;
    /** One or two sentences, visible in the signed-out HTML. */
    intro: (stock?: StockRef) => string;
    /** Exactly three things Advanced adds on this page. */
    unlocks: string[];
    /** Shown under the bullets when the page needs a caveat (e.g. an add-on). */
    note?: string;
    definitions: MetricDefinition[];
    /**
     * The page places "About this data" inside its own layout (`<AboutThisData inPage />`), so the
     * shell leaves it out below <main>. For one-screen pages whose grids fill the window: below the
     * page it would only add a page scroll under them.
     */
    aboutInPage?: boolean;
}

const stockLabel = (stock?: StockRef) => (stock ? `${stock.name} (${stock.exchangesymbol}:${stock.ticker})` : "");

const SHORT_INTEREST_DEFINITIONS: MetricDefinition[] = [
    {
        term: "Short interest",
        definition:
            "The number of shares that have been sold short and not yet bought back, usually shown as a percentage of free float (the shares available to trade). ORTEX estimates it every day from securities-lending data, instead of waiting for exchange reports that are published only twice a month and about two weeks late.",
    },
    {
        term: "Cost to borrow",
        definition:
            "The annualised fee a short seller pays to borrow the shares, as a percentage of the position's value. A rising cost to borrow means lendable shares are getting scarce and holding the short is getting more expensive.",
    },
    {
        term: "Utilization",
        definition: "The share of the lendable supply of a stock that is currently on loan. Close to 100% means almost no shares are left to borrow.",
    },
    {
        term: "Days to cover",
        definition: "Short interest divided by average daily trading volume: roughly how many days of normal trading it would take for every short seller to buy back their shares.",
    },
    {
        term: "Exchange-reported short interest",
        definition:
            "The official figure published by the exchange or regulator (in the US, FINRA twice a month). It is accurate but delayed, which is the gap the ORTEX daily and intraday estimates fill.",
    },
];

const INSIDER_DEFINITIONS: MetricDefinition[] = [
    {
        term: "Insider transaction",
        definition:
            "A purchase or sale of a company's shares by its directors, officers or major shareholders, taken from the official filing (in the US, SEC Form 4, due within two business days of the trade).",
    },
    {
        term: "Open-market purchase",
        definition: "An insider buying shares on the market with their own money, as opposed to receiving them through options, awards or compensation plans. It is the transaction type most read as a signal of conviction.",
    },
    {
        term: "Cluster buy",
        definition: "Several insiders of the same company buying within a short window of each other.",
    },
    {
        term: "Insider track record",
        definition: "How a company's share price moved in the months after a given insider's past trades: whether that person's buying and selling has historically been well timed.",
    },
];

export const LOCKED_PAGES: Record<string, LockedPage> = {
    "/s/[exchange]/[ticker]/short-interest": {
        id: "stock_short_interest",
        metric: "Short Interest",
        headline: (s) => `${s?.ticker ?? "This stock"} short interest, cost to borrow and days to cover`,
        intro: (s) =>
            `This page tracks how heavily ${stockLabel(s) || "this stock"} is being shorted: ORTEX's daily short interest estimate, the cost to borrow the shares, utilization of the lendable supply and days to cover, next to the official exchange-reported figure.`,
        unlocks: [
            "Live intraday short interest estimates, updated through the trading day instead of at the previous close",
            "Every figure on this page unblurred: cost to borrow, utilization, days to cover and the ORTEX Short Score",
            "Full history, not just the last 9 months, to compare today's positioning with past squeezes",
        ],
        definitions: SHORT_INTEREST_DEFINITIONS,
    },
    "/s/[exchange]/[ticker]/insiders": {
        id: "stock_insiders",
        metric: "Insiders",
        headline: (s) => `${s?.ticker ?? "This stock"} insider buying and selling`,
        intro: (s) =>
            `Who inside ${stockLabel(s) || "this company"} is buying or selling its shares, how big the trades are, what each insider still holds, and how the stock moved after their earlier trades, all taken from official filings.`,
        unlocks: [
            "The full transaction list and current insider positions, unblurred",
            "Each insider's track record: how the share price moved after their past trades",
            "Insider filings from 54 markets, so non-US holdings and directors are covered too",
        ],
        definitions: INSIDER_DEFINITIONS,
    },
    "/s/[exchange]/[ticker]/dividend": {
        id: "stock_dividend",
        metric: "Dividend",
        headline: (s) => `${s?.ticker ?? "This stock"} dividends: declared amounts, dates, history and income`,
        intro: (s) =>
            `When ${stockLabel(s) || "this company"} declares its dividends and goes ex, every past payment adjusted for splits, its stated dividend guidance, how well earnings and cash flow cover the payout, and borrowing around record dates. ORTEX dividend forecasts are being added market by market.`,
        unlocks: [
            "Every declared amount with the split-adjusted history, cover, payout ratio and dividend guidance",
            "Borrowing around the record date and the expected price drop on the ex-date",
            "Alerts when a dividend is declared or goes ex, and ORTEX forecasts as each market is added",
        ],
        definitions: [
            {
                term: "Ex-dividend date",
                definition: "The first trading day on which a buyer of the shares no longer receives the next dividend. Holders at the close of the day before are paid.",
            },
            {
                term: "Record and pay date",
                definition: "The record date is when the company checks who holds the shares; the pay date is when the dividend is paid to them.",
            },
            {
                term: "Forward yield",
                definition: "Where ORTEX forecasts are published, the dividends expected over the next twelve months divided by the last share price.",
            },
            {
                term: "Payout ratio",
                definition: "The share of earnings, or of free cash flow, that is paid out as dividends. Above 100% the dividend is paid from more than the company earns.",
            },
        ],
    },
    "/short-interest": {
        id: "short_interest_hub",
        metric: "Short Interest",
        headline: () => "Short interest across global markets",
        intro: () =>
            "The most shorted, most expensive to borrow and fastest-changing short positions across US, European and Asian stocks, from ORTEX's daily short interest estimates rather than twice-monthly exchange reports.",
        unlocks: [
            "Live short screener: filter every stock on short interest, cost to borrow, utilization and days to cover",
            "Intraday changes in short interest, not just the previous close",
            "Full history and every row unblurred across all markets",
        ],
        definitions: SHORT_INTEREST_DEFINITIONS,
    },
    "/insiders": {
        id: "insiders_hub",
        metric: "Insiders",
        headline: () => "Where corporate insiders are buying and selling",
        intro: () => "Insider trades from official filings in 54 markets, ranked by company, sector, market and person, including cluster buys and the insiders whose past trades were best timed.",
        unlocks: [
            "Every trade and every insider across 54 markets, with full history",
            "Insider track records: which directors' past trades were followed by the biggest moves",
            "Cluster buys and sector views as they are filed",
        ],
        definitions: INSIDER_DEFINITIONS,
    },
    "/buybacks": {
        id: "buybacks",
        metric: "Buybacks",
        headline: () => "What companies actually spent on buybacks",
        intro: () =>
            "Quarterly share repurchases as reported in company filings, the authorization each company has left, and the buyback as a percentage of float, so announced programmes can be told apart from money actually spent.",
        unlocks: [
            "Buybacks next to live short interest and cost to borrow, to find shrinking float against a crowded short",
            "ORTEX's full stock pages behind every company on the list: short interest, insiders, options and fundamentals",
            "Full history across every covered market",
        ],
        definitions: [
            {
                term: "Executed buyback",
                definition: "The amount a company actually spent repurchasing its own shares in a period, as reported in its filings (in the US, the 10-Q or 10-K). Announced programmes are statements of intent and are often not completed.",
            },
            {
                term: "Remaining authorization",
                definition: "How much of the repurchase programme approved by the board is still unspent.",
            },
            {
                term: "Buyback as % of float",
                definition: "Shares repurchased as a percentage of the shares available to trade: how much the company is shrinking the tradeable supply.",
            },
        ],
    },
    "/us-gov-trades": {
        id: "us_gov_trades",
        metric: "US Government Trades",
        headline: () => "What members of Congress are buying and selling",
        intro: () => "Stock trades disclosed by US members of Congress and other officials, with the trade date, the disclosure date and the reported value range.",
        unlocks: [
            "The full disclosure history for every official, unblurred",
            "ORTEX's full stock pages behind every traded name: short interest, insiders and options",
            "Live short interest and cost to borrow next to the stocks officials are trading",
        ],
        definitions: [
            {
                term: "STOCK Act disclosure",
                definition: "US law requires members of Congress to report trades over $1,000 within 45 days. Values are disclosed as ranges, not exact amounts.",
            },
            {
                term: "Disclosure lag",
                definition: "The days between the trade itself and the date it was publicly reported.",
            },
        ],
    },
    "/ipo-deals": {
        id: "ipo_deals",
        metric: "IPOs and Equity Deals",
        headline: () => "IPO calendar and equity deal pipeline",
        intro: () => "Upcoming and recent IPOs and follow-on equity offerings with pricing, valuation and lock-up details.",
        unlocks: [
            "Every deal in the pipeline unblurred, with pricing and valuation",
            "Lock-up expiry dates and sizes for recent listings",
            "Short interest and cost to borrow on newly listed stocks",
        ],
        definitions: [
            {
                term: "IPO pricing",
                definition: "The price at which shares are sold to investors in an initial public offering, set the evening before trading starts, often against an earlier indicated range.",
            },
            {
                term: "Follow-on offering",
                definition: "A sale of new or existing shares by a company that is already listed. New shares dilute existing holders.",
            },
            {
                term: "Lock-up",
                definition: "A period after an IPO, typically 90 to 180 days, in which insiders and pre-IPO investors agree not to sell.",
            },
        ],
    },
    "/lock-ups": {
        id: "lock_ups",
        metric: "Lock-up Expiries",
        headline: () => "Lock-up expiries and the supply they release",
        intro: () => "Which restricted shares become sellable and when, sized against free float, average daily volume and market value, so it is clear whether an expiry is big enough to matter.",
        unlocks: [
            "Every expiry unblurred, with shares released as % of float and days of volume",
            "Short interest and cost to borrow on stocks approaching expiry",
            "What past expiries did to the share price",
        ],
        definitions: [
            {
                term: "Lock-up expiry",
                definition: "The date on which insiders and pre-IPO investors are free to sell shares they agreed to hold after a listing, usually 90 to 180 days after the IPO.",
            },
            {
                term: "Shares released as % of float",
                definition: "The newly sellable shares compared with the shares already trading: the larger it is, the more potential supply hits the market.",
            },
        ],
    },
    "/earnings": {
        id: "earnings",
        metric: "Earnings",
        headline: () => "Who reports in the next two weeks",
        intro: () => "Upcoming earnings with each company's historical earnings reactions, the options-implied move, short interest and alternative-data leads.",
        unlocks: [
            "Alternative-data leads on reporting companies: card spend, web traffic and other datasets that move ahead of results",
            "Live short interest and cost to borrow going into each report",
            "Full history of past earnings reactions",
        ],
        definitions: [
            {
                term: "Options-implied move",
                definition: "The share price move, up or down, that options prices imply for the day after earnings, derived from the price of the at-the-money straddle.",
            },
            {
                term: "Earnings reaction",
                definition: "How far the share price moved on the trading day after past earnings reports.",
            },
        ],
    },
    "/earnings/heatmap": {
        id: "earnings_heatmap",
        metric: "Earnings Heatmap",
        headline: () => "Upcoming earnings in one heatmap",
        intro: () => "Upcoming earnings, beats, misses and post-earnings price moves in a single heatmap view.",
        unlocks: [
            "Live short interest and cost to borrow going into each report",
            "Alternative-data leads on reporting companies",
            "Full history of past earnings reactions",
        ],
        definitions: [
            {
                term: "Earnings beat / miss",
                definition: "Whether reported earnings per share came in above or below the analyst consensus estimate.",
            },
            {
                term: "Earnings reaction",
                definition: "How far the share price moved on the trading day after past earnings reports.",
            },
        ],
    },
    "/index-rebalance": {
        id: "index_rebalance",
        metric: "Index Rebalance",
        headline: () => "Index rebalance announcements and forecasts",
        intro: () => "Additions, deletions and weight changes in major equity indexes, with announcement and effective dates and the passive buying or selling each change implies.",
        unlocks: [
            "ORTEX Advanced is the plan the Index Rebalance Forecasts add-on is built on",
            "Short interest and cost to borrow on every stock moving in or out of an index",
            "ORTEX's full stock pages behind every name on the list",
        ],
        note: "Forecasts of upcoming index changes are a separate add-on for Advanced subscribers.",
        aboutInPage: true,
        definitions: [
            {
                term: "Index rebalance",
                definition: "A scheduled review in which an index provider adds, removes or reweights constituents. Funds tracking the index then have to buy or sell to match it.",
            },
            {
                term: "Announcement and effective date",
                definition: "The day the provider publishes the change, and the day it takes effect. Index funds trade at the close before the effective date.",
            },
            {
                term: "Passive demand",
                definition: "The estimated value of shares index-tracking funds must buy (or sell), often expressed in days of average trading volume.",
            },
        ],
    },
    "/dividends": {
        id: "dividends",
        metric: "Dividends",
        headline: () => "Dividend calendar and income: what every company has declared, and when it pays",
        intro: () =>
            "Declared dividends, ex-dates and pay dates across global markets, with each company's dividend history adjusted for splits and its stated dividend guidance. Dates are open to everyone. ORTEX dividend forecasts are being added market by market.",
        unlocks: [
            "Every declared amount next to the company's history, cover and dividend guidance, and the expected price drop on each ex-date",
            "Your portfolio's dividend income month by month, net of withholding tax for where you live, with reinvestment",
            "Alerts when a dividend is declared or goes ex, a weekly dividend digest, and ORTEX forecasts as each market is added",
        ],
        aboutInPage: true,
        definitions: [
            {
                term: "Ex-dividend date",
                definition:
                    "The first trading day on which a buyer of the shares no longer receives the next dividend. To be paid, you must own the shares before the ex-date; the share price usually drops by about the dividend on that morning.",
            },
            {
                term: "Record date",
                definition:
                    "The day the company checks its register to see who is paid. With one-day settlement (US and Canada since May 2024) it is usually the same day as the ex-date; in markets that settle in two days it is the business day after.",
            },
            {
                term: "Forecast range",
                definition:
                    "Where ORTEX forecasts are published, the amount ORTEX expects the company to declare, with a low and a high figure around it. How often declared amounts fell inside the range is shown on the track record.",
            },
            {
                term: "Chance of a change",
                definition:
                    "Where ORTEX forecasts are published, how likely the next dividend is to differ from the last one: the chance of a raise plus the chance of a cut or an omission.",
            },
        ],
    },
    "/stock-movers": {
        id: "stock_movers",
        metric: "Stock Movers",
        headline: () => "Today's biggest gainers, losers and most active stocks",
        intro: () => "The stocks moving most today across global markets and sectors, with volume against normal levels.",
        unlocks: [
            "Short interest and cost to borrow next to every mover, to see when a move is a squeeze",
            "ORTEX's full stock pages behind every name: insiders, options and fundamentals",
            "Movers across every covered market, not just the US",
        ],
        definitions: [
            {
                term: "Relative volume",
                definition: "Today's trading volume compared with the stock's average daily volume. Moves on high relative volume carry more weight.",
            },
            {
                term: "Short squeeze",
                definition: "A sharp rise in a heavily shorted stock as short sellers buy back shares to limit losses, which pushes the price higher still.",
            },
        ],
    },
};

export const getLockedPage = (pathname: string): LockedPage | undefined => LOCKED_PAGES[pathname];

/** Canonical absolute URL for the page, matching the page's own `<link rel="canonical">`. */
export const lockedPageUrl = (pathname: string, stock?: StockRef): string => {
    const origin = "https://app.ortex.com";
    if (stock && pathname.startsWith("/s/[exchange]/[ticker]")) {
        return `${origin}${pathname.replace("[exchange]", stock.exchangesymbol).replace("[ticker]", stock.ticker)}`;
    }
    return `${origin}${pathname}`;
};
